import {
  updateStudentStatus,
  deleteStudent,
  resetStudentPassword,
} from '../../../api/utils/lms/student/api';
import { getErrorMessages } from '../../../api/utils/util-func';
import {
  StudentStatusEnum,
  studentType,
} from '../../../constants/types/student-type';
import { AppDispatch } from '../../../store';
import {
  openAlert,
  openConfirm,
  openModal,
} from '../../../store/features/modalSlice';
import NewPasswordModalContent from './popup/NewPasswordModalContent';
import { NavigateFunction } from 'react-router-dom';

class StudentConroller {
  private dispatch: AppDispatch;
  private classId: string;
  private reloadTable: () => void;
  /** 반 배정 팝업 열기. 팝업은 '신규 학생 등록'과 같은 FormDialog 형태라 상위에서 상태로 연다. */
  private openAssignModal: (studentList: studentType[]) => void;
  /** 이관 신청 팝업 열기. 이관은 학생 한 명씩만 신청한다. */
  private openTransferModal: (student: studentType) => void;
  private navigate: NavigateFunction;

  constructor(
    dispatch: AppDispatch,
    classId: string,
    reloadTable: () => void,
    openAssignModal: (studentList: studentType[]) => void,
    openTransferModal: (student: studentType) => void,
    navigate: NavigateFunction,
  ) {
    this.dispatch = dispatch;
    this.classId = classId;
    this.reloadTable = reloadTable;
    this.openAssignModal = openAssignModal;
    this.openTransferModal = openTransferModal;
    this.navigate = navigate;
  }

  /**
   * 학생 상세 페이지 이동
   * @param id
   */
  public detailHandler = (id: number) => {
    this.navigate(`./${id}`);
  };

  /**
   * 타입에 따른 메시지 생성
   * @param studentList
   * @param type
   * @returns
   */
  private makeMsg = (
    studentList: studentType[],
    type: '정지' | '삭제' | '수강 등록',
  ) => {
    const count = studentList.length;

    if (count === 1) {
      return `${studentList[0].name} 학생을 ${type} 하시겠습니까?`;
    } else {
      return `총 ${count}명의 학생을 ${type} 하시겠습니까?`;
    }
  };

  /**
   * 학생 상태 변경
   * @param studentList
   * @param status
   */
  private changeStatus = (
    studentList: studentType[],
    status: StudentStatusEnum,
  ) => {
    updateStudentStatus(
      this.classId,
      studentList.map((item) => item.id),
      status,
    )
      .then(() => {
        this.reloadTable();
      })
      .catch((e) => {
        this.dispatch(openAlert({ message: getErrorMessages(e) }));
      });
  };

  /**
   * 체크된 학생 수 확인
   * @param studentList
   * @returns
   */
  private confirmCheckedCount = (studentList: studentType[]): boolean => {
    if (studentList.length === 0) {
      this.dispatch(openAlert({ message: '학생을 선택해주세요.' }));
      return false;
    }
    return true;
  };

  /**
   * 확인창
   * @param msg
   * @param onConfirm
   */
  private confirm(msg: string, onConfirm: () => void) {
    this.dispatch(
      openConfirm({
        message: msg,
        onConfirm: onConfirm,
      }),
    );
  }

  /**
   * 학생 반 배정
   * @param studentList
   * @returns
   */
  public assignHandler = (studentList: studentType[]) => {
    if (!this.confirmCheckedCount(studentList)) return;

    this.openAssignModal(studentList);
  };

  /**
   * 학생 이관 신청
   * @param student 이관 대상 학생
   * @param hasPendingRequest 이미 처리 대기 중인 신청이 있는지 여부
   */
  public transferHandler = (
    student: studentType,
    hasPendingRequest: boolean,
  ) => {
    if (hasPendingRequest) {
      this.dispatch(
        openAlert({
          message:
            '이미 처리 대기 중인 이관 신청이 있습니다.\n‘이관 신청 내역’에서 진행 상태를 확인해주세요.',
        }),
      );
      return;
    }

    this.openTransferModal(student);
  };

  /**
   * 학생 정지
   * @param studentList
   * @returns
   */
  public stopHandler = (studentList: studentType[]) => {
    if (!this.confirmCheckedCount(studentList)) return;

    const msg = this.makeMsg(studentList, '정지');
    const onConfirm = () =>
      this.changeStatus(studentList, StudentStatusEnum.INACTIVE);
    this.confirm(msg, onConfirm);
  };

  /**
   * 학생 수강등록
   * @param studentList
   * @returns
   */
  public registHandler = (studentList: studentType[]) => {
    if (!this.confirmCheckedCount(studentList)) return;

    const msg = this.makeMsg(studentList, '수강 등록');
    const onConfirm = () =>
      this.changeStatus(studentList, StudentStatusEnum.ACTIVE);
    this.confirm(msg, onConfirm);
  };

  /**
   * 비밀번호 초기화
   * @param id
   */
  public resetHandler = (student: studentType) => {
    const msg = `${student.name} 학생의 비밀번호를 초기화 하시겠습니까?`;
    const onConfirm = () => {
      resetStudentPassword(this.classId, student.id).then(
        ({ uid, password }) => {
          this.dispatch(
            openModal({
              body: (
                <NewPasswordModalContent type="RESET" uid={uid} pw={password} />
              ),
            }),
          );
        },
      );
    };
    this.confirm(msg, onConfirm);
  };

  /**
   * 학생 삭제
   * @param studentList
   * @returns
   */
  public deleteHandler = (studentList: studentType[]) => {
    if (!this.confirmCheckedCount(studentList)) return;

    // 1차 삭제 메시지
    const msg = this.makeMsg(studentList, '삭제');

    // 최종 삭제
    const deleteAction = () => {
      deleteStudent(
        this.classId,
        studentList.map((item) => item.id),
      );
    };

    // 2차 삭제 확인
    const onConfirm = () => {
      // 2차 삭제 메시지
      const tmpMsg =
        '학생 삭제 시 학생의 모든 학습 이력 및 데이터가 삭제되며, 추후 복구가 불가합니다.\nㅤ\n정말 삭제하시겠습니까?';
      this.confirm(tmpMsg, deleteAction);
    };

    // 1차 삭제 확인
    this.confirm(msg, onConfirm);
  };
}

export default StudentConroller;
